import { createFileRoute } from "@tanstack/react-router";
import SimpleTable from "@/components/ui/table";
import { useSimpleForm } from "@/hooks/use-simple-form";
import { useEffect } from "react";
import FullPagination from "@/components/ui/shared/full-pagination";
import {
  FinanceAccountStatusOptions,
  FinanceAccountTableColumns,
  FinanceAccountTypeOptions,
} from "@/components/finance-account/finance-account-meta";
import { SearchEngine } from "@/components/ui/shared/search-engine";
import { Wallet } from "lucide-react";

import type { FinanceAccount } from "@/store/FinanceAccount";
import { useTranslate } from "@/hooks/use-translate";
import { FinanceAccountModals } from "@/components/finance-account/finance-account-modals";
import { Button } from "@/components/ui/button";
import useAppStore from "@/store";
import { Plus } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/(dasboard)/finance/accounts/")({
  component: () => <IncomesPage />,
});

function IncomesPage() {
  const store = useAppStore();
  const { t } = useTranslate();
  const navigate = useNavigate();

  const searchForm = useSimpleForm({
    keyword: "",
    type: undefined,
    status: undefined,
  });

  const search = ({ page = 1, per_page = 10 } = {}) => {
    store.financeAccount.fetch({
      page,
      per_page,
      status: undefined,
      ...searchForm.values,
    });
  };

  useEffect(() => {
    if (store.auth.session) search({});
  }, [store.auth.session]);

  return (
    <>
      <FinanceAccountModals />
      <div className="max-w-7xl mx-auto">
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
          <h2 className="text-xl font-semibold"> {t("Treasury accounts")} </h2>
          <Button
            onClick={() => {
              store.display.show("create_finance_account");
            }}
          >
            <Plus className="w-4 h-4" />
            {t("Add a treasury account")}
          </Button>
        </header>

        <div className="mb-4">
          <SearchEngine
            autoComplete={(values: any) => {
              store.financeAccount.paginate(
                store.financeAccount.items.filter(
                  (acc: FinanceAccount) =>
                    acc.name
                      .toLowerCase()
                      .includes(values.keyword.toLowerCase()) &&
                    (values.type ? acc.type === values.type : true) &&
                    (values.status ? acc.status == values.status : true)
                )
              );
            }}
            selects={[
              {
                placeholder: t("All types"),
                options: FinanceAccountTypeOptions,
                name: "type",
                size: "w-full sm:w-[250px]",
                icon: <Wallet className="w-4 h-4" />,
              },
              {
                placeholder: t("All statuses"),
                options: FinanceAccountStatusOptions,
                name: "status",
                size: "w-full sm:w-[250px]",
                icon: <Wallet className="w-4 h-4" />,
              },
            ]}
            form={searchForm}
            onSubmit={undefined}
          />
        </div>

        <SimpleTable
          items={store.financeAccount.pagination.data}
          columns={FinanceAccountTableColumns({
            store,
            onView: (acc: any) => {
              store.financeAccount.setCurrent(acc);
              navigate({ to: `/finance/accounts/${acc.id}` });
            },
            onEdit: (acc: any) => {
              store.financeAccount.setCurrent(acc);
              store.display.show("edit_finance_account");
            },
            onDelete: async (acc: any) => {
              store.financeAccount.setCurrent(acc);
              store.display.show("delete_finance_account");
            },
          })}
        />

        <FullPagination
          className="mt-4"
          canEdit={true}
          pagination={store.financeAccount.pagination}
          onGoto={(page: number) => search({ page })}
          onPerPage={(per_page) => search({ per_page })}
        />
      </div>
    </>
  );
}
